import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import Navbar from "@/components/layout/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const questions = [
  {
    id: "category",
    title: "Hva leter du etter?",
    options: [
      { value: "driver", label: "Driver" },
      { value: "wood", label: "Wood" },
      { value: "hybrid", label: "Hybrid" },
      { value: "jern", label: "Jern" },
      { value: "wedge", label: "Wedge" },
      { value: "putter", label: "Putter" },
      { value: "bag", label: "Bag" },
      { value: "komplett", label: "Komplett sett" },
      { value: "all", label: "Åpen for alt" },
    ],
  },
  {
    id: "gender",
    title: "Kjønn",
    options: [
      { value: "herre", label: "Herre" },
      { value: "dame", label: "Dame" },
    ],
  },
  {
    id: "height",
    title: "Hvor høy er du?",
    options: [
      { value: "under165", label: "Under 165 cm" },
      { value: "165-175", label: "165 - 175 cm" },
      { value: "175-185", label: "175 - 185 cm" },
      { value: "185-195", label: "185 - 195 cm" },
      { value: "over195", label: "Over 195 cm" },
    ],
  },
  {
    id: "handicap",
    title: "Hva er ditt handicap?",
    options: [
      { value: "none", label: "Har ikke HCP ennå" },
      { value: "36+", label: "36+" },
      { value: "25-36", label: "25 - 36" },
      { value: "15-24", label: "15 - 24" },
      { value: "5-14", label: "5 - 14" },
      { value: "under5", label: "Under 5" },
    ],
  },
  {
    id: "hand",
    title: "Hvilket håndsett spiller du?",
    options: [
      { value: "høyre", label: "Høyrehendt" },
      { value: "venstre", label: "Venstrehendt" },
    ],
  },
  {
    id: "tempo",
    title: "Slår du langt sammenlignet med vennene dine?",
    subtitle: "Dette hjelper oss å anbefale riktig flex på skaftet",
    options: [
      { value: "kort", label: "Kortere enn de fleste" },
      { value: "middels", label: "Omtrent likt" },
      { value: "lang", label: "Lengre enn de fleste" },
    ],
  },
  {
    id: "budget",
    title: "Hva er budsjettet ditt?",
    options: [
      { value: "under1000", label: "Under 1 000 kr" },
      { value: "1000-3000", label: "1 000 - 3 000 kr" },
      { value: "3000-5000", label: "3 000 - 5 000 kr" },
      { value: "over5000", label: "Over 5 000 kr" },
    ],
  },
];

// Matching logic
const getFlexRecommendation = (gender, tempo, handicap) => {
  // Ladies typically use more flexible shafts
  if (gender === "dame") {
    if (tempo === "lang") return ["regular", "stiff"];
    return ["lady", "regular"];
  }
  
  // Men
  if (handicap === "under5" || handicap === "5-14") {
    if (tempo === "lang") return ["x-stiff", "stiff"];
    if (tempo === "middels") return ["stiff", "regular"];
    return ["regular", "stiff"];
  }
  
  if (tempo === "lang") return ["stiff", "x-stiff"];
  if (tempo === "middels") return ["regular", "stiff"];
  return ["regular", "senior"];
};

const getBudgetRange = (budget) => {
  switch (budget) {
    case "under1000": return { min: 0, max: 1000 };
    case "1000-3000": return { min: 1000, max: 3000 };
    case "3000-5000": return { min: 3000, max: 5000 };
    case "over5000": return { min: 5000, max: 999999 };
    default: return { min: 0, max: 999999 };
  }
};

const getRecommendationReason = (listing, answers) => {
  const reasons = [];
  
  // Flex match
  const recommendedFlex = getFlexRecommendation(answers.gender, answers.tempo, answers.handicap);
  if (listing.flex && recommendedFlex.includes(listing.flex.toLowerCase())) {
    if (answers.tempo === "lang") {
      reasons.push(`${listing.flex} flex passer ditt raske sving`);
    } else if (answers.tempo === "kort") {
      reasons.push(`${listing.flex} flex gir deg mer fart på ballen`);
    } else {
      reasons.push(`${listing.flex} flex passer ditt svingtempo`);
    }
  }
  
  // Hand match
  if (listing.hand && listing.hand.toLowerCase() === answers.hand) {
    reasons.push(`tilgjengelig for ${answers.hand === "høyre" ? "høyrehendte" : "venstrehendte"}`);
  }
  
  // Budget match
  const budgetRange = getBudgetRange(answers.budget);
  if (listing.price <= budgetRange.max) {
    if (listing.price < budgetRange.max * 0.7) {
      reasons.push("godt under budsjettet ditt");
    } else {
      reasons.push("innenfor budsjettet");
    }
  }
  
  // Handicap-based reasons
  if (answers.handicap === "none" || answers.handicap === "36+") {
    reasons.push("passer for nybegynnere som trenger tilgivende utstyr");
  } else if (answers.handicap === "under5" || answers.handicap === "5-14") {
    reasons.push("kvalitetsutstyr for erfarne spillere");
  }
  
  // Category match
  if (listing.category === answers.category || answers.category === "all") {
    // Already filtered, no need to mention
  }
  
  if (reasons.length === 0) {
    reasons.push("kan passe dine behov");
  }
  
  // Combine into a sentence
  const combined = reasons.slice(0, 2).join(" og ");
  return combined.charAt(0).toUpperCase() + combined.slice(1) + ".";
};

const calculateMatchScore = (listing, answers) => {
  let score = 0;
  
  // Category match (high priority)
  if (answers.category === "all" || listing.category === answers.category) {
    score += 30;
  }
  
  // Budget match
  const budgetRange = getBudgetRange(answers.budget);
  if (listing.price >= budgetRange.min && listing.price <= budgetRange.max) {
    score += 25;
  } else if (listing.price < budgetRange.min) {
    score += 15; // Under budget is still good
  }
  
  // Flex match
  const recommendedFlex = getFlexRecommendation(answers.gender, answers.tempo, answers.handicap);
  if (listing.flex && recommendedFlex.includes(listing.flex.toLowerCase())) {
    score += 20;
  }
  
  // Hand match
  if (listing.hand && listing.hand.toLowerCase() === answers.hand) {
    score += 15;
  }
  
  // Condition bonus for newer players
  if ((answers.handicap === "none" || answers.handicap === "36+") && listing.condition === "god") {
    score += 5;
  }
  
  return score;
};

const EquipmentGuidePage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const progress = ((currentStep) / questions.length) * 100;

  const handleAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleNext = async () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      // Submit and get results
      await fetchRecommendations();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      // Fetch all active listings
      const response = await axios.get(`${API}/listings`, {
        params: { status: "active", limit: 100 },
      });
      
      let listings = response.data;
      
      // Filter by category if not "all"
      if (answers.category !== "all" && answers.category !== "komplett") {
        listings = listings.filter(
          (l) => l.category === answers.category || !l.category
        );
      }
      
      // Filter by budget (allow some flexibility)
      const budgetRange = getBudgetRange(answers.budget);
      listings = listings.filter(
        (l) => l.price <= budgetRange.max * 1.2 // Allow 20% over budget
      );
      
      // Calculate match scores and sort
      const scoredListings = listings.map((listing) => ({
        ...listing,
        matchScore: calculateMatchScore(listing, answers),
        reason: getRecommendationReason(listing, answers),
      }));
      
      // Sort by match score (highest first)
      scoredListings.sort((a, b) => b.matchScore - a.matchScore);
      
      // Take top 10
      setResults(scoredListings.slice(0, 10));
    } catch (error) {
      console.error("Error fetching recommendations:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetGuide = () => {
    setCurrentStep(0);
    setAnswers({});
    setResults(null);
  };

  const currentQuestion = questions[currentStep];
  const isAnswered = answers[currentQuestion?.id];

  // Show results
  if (results !== null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 pb-24">
        {/* Header */}
        <Navbar showBackButton={true} backTo="/" showWedgeGuide={false} />

        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Dine anbefalinger
            </h1>
            <p className="text-gray-600">
              Basert på dine svar tror vi dette utstyret kan passe deg godt
            </p>
          </div>

          {results.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-600 mb-4">
                Vi fant dessverre ingen annonser som matcher dine kriterier akkurat nå.
              </p>
              <Button onClick={() => navigate("/")}>
                Se alle annonser
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {results.map((listing, index) => (
                <Card 
                  key={listing.id} 
                  className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/listing/${listing.id}`)}
                >
                  <div className="flex">
                    {/* Image */}
                    <div className="w-32 h-32 sm:w-48 sm:h-48 flex-shrink-0">
                      {listing.images && listing.images.length > 0 ? (
                        <img
                          src={listing.images[0]}
                          alt={listing.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <span className="text-gray-400">Ingen bilde</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <CardContent className="flex-1 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="bg-emerald-100 text-emerald-700 text-xs font-medium px-2 py-0.5 rounded">
                              #{index + 1} match
                            </span>
                          </div>
                          <h3 className="font-semibold text-lg text-gray-900">
                            {listing.title}
                          </h3>
                          <p className="text-emerald-600 font-bold text-xl mt-1">
                            {listing.price.toLocaleString("nb-NO")} kr
                          </p>
                          <p className="text-gray-600 text-sm mt-2 italic">
                            "{listing.reason}"
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </div>
                </Card>
              ))}
            </div>
          )}

          <div className="mt-8 text-center">
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              className="mr-4"
            >
              Se alle annonser
            </Button>
            <Button onClick={resetGuide}>
              Ta guiden på nytt
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-gray-600">Leter etter utstyr som passer deg...</p>
        </div>
      </div>
    );
  }

  // Show questions
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 pb-24">
      {/* Header */}
      <Navbar showBackButton={true} backTo="/" showWedgeGuide={false} />

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Title - only show on first step */}
        {currentStep === 0 && (
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Hvilket utstyr passer meg?
            </h1>
            <p className="text-gray-600">
              Svar på noen få spørsmål - vi viser deg brukt utstyr som passer ditt nivå og spillestil.
            </p>
          </div>
        )}

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-gray-500 mb-2">
            <span>Spørsmål {currentStep + 1} av {questions.length}</span>
            <span>{Math.round(progress)}% fullført</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question */}
        <Card className="p-6 sm:p-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">
            {currentQuestion.title}
          </h2>
          {currentQuestion.subtitle && (
            <p className="text-gray-500 text-sm mb-4">{currentQuestion.subtitle}</p>
          )}
          
          <div className="grid gap-3 mt-6">
            {currentQuestion.options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleAnswer(currentQuestion.id, option.value)}
                className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                  answers[currentQuestion.id] === option.value
                    ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{option.label}</span>
                  {answers[currentQuestion.id] === option.value && (
                    <Check className="w-5 h-5 text-emerald-600" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Tilbake
          </Button>
          <Button
            onClick={handleNext}
            disabled={!isAnswered}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {currentStep === questions.length - 1 ? (
              <>
                Se anbefalinger
                <Check className="w-4 h-4 ml-2" />
              </>
            ) : (
              <>
                Neste
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EquipmentGuidePage;
